import React from 'react';
import { X, CheckCircle, Calendar, Clock, User, Package, Printer, MapPin, UserCheck } from 'lucide-react';
import { Button } from '../ui/Button';
import { Reservation, InventoryItem } from '../../types';

interface ColorSize {
  id: string;
  name: string;
}

interface ReservationConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  reservation: Reservation | null;
  items: InventoryItem[];
  colors: ColorSize[];
  sizes: ColorSize[];
  createdByName: string;
}

export const ReservationConfirmation: React.FC<ReservationConfirmationProps> = ({
  isOpen,
  onClose,
  reservation,
  items,
  colors,
  sizes,
  createdByName
}) => {
  if (!isOpen || !reservation) return null;

  const expiresAt = new Date(reservation.expires_at);

  const getColorName = (colorId?: string) => {
    if (!colorId) return '-';
    return colors.find(c => c.id === colorId)?.name || '-';
  };

  const getSizeName = (sizeId?: string) => {
    if (!sizeId) return '-';
    return sizes.find(s => s.id === sizeId)?.name || '-';
  };

  const getProductInfo = (productId: string) => {
    return items.find(item => item.id === productId);
  };

  const printReservation = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const itemsHtml = reservation.reservation_items?.map(item => {
      const product = getProductInfo(item.product_id);
      return `
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${product?.name || 'Unknown'}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${product?.reference_code || '-'}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${getSizeName(product?.size_id)}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${getColorName(product?.color_id)}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${product?.category || '-'}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: center;">${item.quantity}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${product?.location || '-'}</td>
        </tr>
      `;
    }).join('') || '<tr><td colspan="7" style="padding: 20px; text-align: center;">No items</td></tr>';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Reservation - ${reservation.reservation_number}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; color: #1f2937; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; padding-bottom: 24px; border-bottom: 2px solid #e5e7eb; }
          .title { font-size: 28px; font-weight: 700; color: #111827; }
          .subtitle { color: #6b7280; margin-top: 4px; }
          .badge { display: inline-block; padding: 6px 16px; border-radius: 9999px; font-size: 12px; font-weight: 600; text-transform: uppercase; background: #d1fae5; color: #065f46; }
          .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; margin-bottom: 32px; }
          .info-card { background: #f9fafb; border-radius: 12px; padding: 16px; }
          .info-label { font-size: 12px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px; }
          .info-value { font-size: 16px; font-weight: 600; color: #111827; }
          .section-title { font-size: 16px; font-weight: 600; color: #374151; margin-bottom: 16px; }
          .table-container { border-radius: 12px; overflow: hidden; border: 1px solid #e5e7eb; margin-bottom: 24px; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #f3f4f6; padding: 12px 10px; text-align: left; font-size: 11px; font-weight: 600; color: #374151; text-transform: uppercase; letter-spacing: 0.05em; }
          th:nth-child(5) { text-align: center; }
          td { padding: 10px; font-size: 14px; }
          .totals { max-width: 280px; margin-left: auto; }
          .total-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 14px; }
          .total-row.final { font-weight: 700; font-size: 16px; border-top: 2px solid #e5e7eb; padding-top: 12px; margin-top: 8px; }
          .footer { margin-top: 40px; padding-top: 24px; border-top: 1px solid #e5e7eb; text-align: center; color: #9ca3af; font-size: 12px; }
          .notice { background: #fef3c7; padding: 16px; border-radius: 12px; margin-top: 24px; }
          .notice-title { font-weight: 600; color: #92400e; margin-bottom: 8px; }
          .notice-text { color: #78350f; font-size: 14px; }
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="title">Reservation Slip</div>
            <div class="subtitle">${reservation.reservation_number}</div>
          </div>
          <span class="badge">Reserved</span>
        </div>

        <div class="info-grid">
          <div class="info-card">
            <div class="info-label">Customer Name</div>
            <div class="info-value">${reservation.customer_name}</div>
          </div>
          <div class="info-card">
            <div class="info-label">Reserved Date</div>
            <div class="info-value">${new Date(reservation.reserved_at).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
          </div>
          ${reservation.customer_phone ? `
          <div class="info-card">
            <div class="info-label">Phone</div>
            <div class="info-value">${reservation.customer_phone}</div>
          </div>
          ` : ''}
          <div class="info-card">
            <div class="info-label">Pickup Deadline</div>
            <div class="info-value">${expiresAt.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
          </div>
          <div class="info-card">
            <div class="info-label">Created By</div>
            <div class="info-value">${createdByName}</div>
          </div>
        </div>

        <div class="section-title">Reservation Items</div>
        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Reference</th>
                <th>Size</th>
                <th>Color</th>
                <th>Category</th>
                <th>Qty</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHtml}
            </tbody>
          </table>
        </div>

        <div class="totals">
          <div class="total-row">
            <span>Subtotal</span>
            <span>\u20AC${reservation.subtotal.toFixed(2)}</span>
          </div>
          <div class="total-row">
            <span>Tax</span>
            <span>\u20AC${reservation.tax_amount.toFixed(2)}</span>
          </div>
          <div class="total-row final">
            <span>Total Due at Pickup</span>
            <span>\u20AC${reservation.total_amount.toFixed(2)}</span>
          </div>
        </div>

        <div class="notice">
          <div class="notice-title">Important Information</div>
          <div class="notice-text">
            Please bring this confirmation when picking up your items.
            Reservations not picked up by ${expiresAt.toLocaleDateString()} will be automatically cancelled.
          </div>
        </div>

        <div class="footer">
          <p>Printed on ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full overflow-hidden max-h-[90vh] flex flex-col">
        <div className="p-6 bg-gradient-to-br from-green-500 to-emerald-600 text-white flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8" />
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <h2 className="text-2xl font-bold mb-1">Reservation Created!</h2>
          <p className="text-green-100">Items have been reserved for the customer</p>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 mb-6">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
              Reservation Number
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 tracking-wider">
              {reservation.reservation_number}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <User className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400">Customer</p>
                <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                  {reservation.customer_name}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <UserCheck className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400">Created By</p>
                <p className="font-medium text-blue-600 dark:text-blue-400 truncate">
                  {createdByName}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400">Reserved On</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {new Date(reservation.reserved_at).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400">Pickup Deadline</p>
                <p className="font-medium text-amber-600 dark:text-amber-400">
                  {expiresAt.toLocaleDateString()} ({Math.ceil((expiresAt.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} days)
                </p>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Package className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                Reserved Items ({reservation.reservation_items?.length || 0})
              </h3>
            </div>
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-900">
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Name</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Reference</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Size</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Color</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Category</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Qty</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Location</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {reservation.reservation_items?.map((item, idx) => {
                    const product = getProductInfo(item.product_id);
                    return (
                      <tr key={idx} className="bg-white dark:bg-gray-800">
                        <td className="px-3 py-2 text-gray-900 dark:text-gray-100 font-medium">
                          {product?.name || 'Unknown Product'}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                          {product?.reference_code || '-'}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                          {getSizeName(product?.size_id)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                          {getColorName(product?.color_id)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                          {product?.category || '-'}
                        </td>
                        <td className="px-3 py-2 text-center text-gray-900 dark:text-gray-100 font-medium">
                          {item.quantity}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {product?.location || '-'}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {(!reservation.reservation_items || reservation.reservation_items.length === 0) && (
                    <tr>
                      <td colSpan={7} className="px-3 py-6 text-center text-gray-500 dark:text-gray-400">
                        No items in this reservation
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Subtotal</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {'\u20AC'}{reservation.subtotal.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm text-gray-600 dark:text-gray-400">Tax</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {'\u20AC'}{reservation.tax_amount.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-gray-700">
              <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                Total Due at Pickup
              </span>
              <span className="text-2xl font-bold text-green-600 dark:text-green-400">
                {'\u20AC'}{reservation.total_amount.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={printReservation}
              className="flex-1"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button
              variant="primary"
              onClick={onClose}
              className="flex-1"
            >
              Done
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
